/**
 * MAP tracking integration for apps/website.
 * Mirrors the JS SDK API but calls the /public/track endpoint directly
 * without loading the external JS file.
 */

const COOKIE_AID = '_map_aid';
const SS_SID = '_map_sid';
const COOKIE_TTL = 365 * 24 * 3600;

function uuid(): string {
  return crypto.randomUUID();
}

function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
  return match ? decodeURIComponent(match[1]) : null;
}

function setCookie(name: string, value: string, maxAge: number): void {
  document.cookie = `${name}=${encodeURIComponent(value)}; max-age=${maxAge}; path=/; SameSite=Lax`;
}

function getOrCreateAid(): string {
  let aid = getCookie(COOKIE_AID);
  if (!aid) { aid = uuid(); setCookie(COOKIE_AID, aid, COOKIE_TTL); }
  return aid;
}

function getOrCreateSid(): string {
  try {
    let sid = sessionStorage.getItem(SS_SID);
    if (!sid) { sid = uuid(); sessionStorage.setItem(SS_SID, sid); }
    return sid;
  } catch { return uuid(); }
}

// ─── Tracker class ─────────────────────────────────────────────────────────────

export class Tracker {
  private readonly writeKey: string;
  private readonly apiUrl: string;
  private customerId: string | null = null;

  constructor(writeKey: string, apiUrl = '') {
    this.writeKey = writeKey;
    this.apiUrl = apiUrl.replace(/\/$/, '') || '';
  }

  private send(type: string, extra: Record<string, unknown>): void {
    const body = JSON.stringify({
      writeKey: this.writeKey,
      type,
      anonymousId: getOrCreateAid(),
      sessionId: getOrCreateSid(),
      ...(this.customerId ? { customerId: this.customerId } : {}),
      ...extra,
    });

    const url = `${this.apiUrl}/public/track`;
    if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
      navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
    } else {
      fetch(url, { method: 'POST', body, headers: { 'Content-Type': 'application/json' } })
        .catch(() => {});
    }
  }

  page(overrides: Record<string, unknown> = {}): void {
    this.send('page', {
      properties: {
        url: window.location.href,
        title: document.title,
        referrer: document.referrer,
        ...overrides,
      },
    });
  }

  identify(customerId: string): void {
    this.customerId = customerId;
    this.send('identify', { customerId, properties: {} });
  }

  track(event: string, props: Record<string, unknown> = {}): void {
    this.send('track', { event, properties: props });
  }
}

// Singleton — initialized in SiteContext
let _tracker: Tracker | null = null;

export function initTracker(writeKey: string): Tracker {
  _tracker = new Tracker(writeKey);
  return _tracker;
}

export function getTracker(): Tracker | null {
  return _tracker;
}
